import * as THREE from 'three';

/**
 * A cutout: a photo (or a drawing) on a flat card that's transparent where the
 * picture is — two triangles however detailed the image, South Park by way of
 * the photo album. `facing: 'camera'` turns the card about its own vertical axis
 * to face the viewer each frame (a person, a tree); `'fixed'` keeps its yaw (a
 * sign, a scene). Shadows take the cutout's shape (alpha-tested depth pass).
 */
export class Cutout {
  readonly root = new THREE.Group();
  /** the card, once the picture has loaded */
  mesh: THREE.Mesh | null = null;

  constructor(
    url: string,
    /** height of the card in metres */
    private height: number,
    private facing: 'camera' | 'fixed' = 'camera',
    /** `userData.tapName` for the picker */
    name = 'cutout',
    /** how far above the ground the bottom edge sits */
    private lift = 0,
  ) {
    new THREE.TextureLoader().load(url, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
      const img = tex.image as { width: number; height: number };
      const w = this.height * (img.width / img.height);
      const mat = new THREE.MeshStandardMaterial({ map: tex, alphaTest: 0.5, roughness: 1, side: THREE.DoubleSide });
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, this.height), mat);
      m.position.y = this.lift + this.height / 2;
      m.castShadow = true;
      m.receiveShadow = true;
      m.customDepthMaterial = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, map: tex, alphaTest: 0.5 });
      m.userData.tapName = name;
      this.root.add(m);
      this.mesh = m;
    });
  }

  /** call each frame: a camera-facing card turns to look at you (about its vertical axis only, so it never tilts) */
  update(cameraPos: THREE.Vector3): void {
    if (this.facing !== 'camera') return;
    this.root.rotation.y = Math.atan2(cameraPos.x - this.root.position.x, cameraPos.z - this.root.position.z);
  }
}
